"use client"

import { cn } from "@/lib/utils"
import { useRouter } from "next/router"
import { FC, useState } from "react"
import { DeleteIcon } from "@/components/shared/icons"
import Modal from "@/components/Modal"
import IconButton from "@/components/IconButton"
import { useDeleteConversationMutation } from "@/queries/ai-agent/useDeleteConversationMutation"
import { useToast } from "@/hooks"
import { isMobileOnly } from "react-device-detect"

interface ChatHistoryItemProps {
  id: string
  title: string
  onClick?: () => void
  timestamp: string
  lastMessage?: string
  avatarUrl?: string
  isActive?: boolean
}

const formatTimestamp = (timestamp: string | number) => {
  // Convert string timestamp to number if needed
  const timestampNum = typeof timestamp === "string" ? parseInt(timestamp) : timestamp

  // Convert seconds to milliseconds for Unix timestamps
  const date = new Date(timestampNum * 1000)
  const now = new Date()
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

  if (diffInMinutes < 1) return "Just now"
  if (diffInMinutes < 60) return `${diffInMinutes}m`

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) return `${diffInHours}h`

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) return `${diffInDays}d`

  // For older dates, show the actual date
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })
}

export const ChatHistoryItem: FC<ChatHistoryItemProps> = ({
  id,
  onClick,
  title,
  lastMessage,
  timestamp,
  avatarUrl,
  isActive = false,
}) => {
  const router = useRouter()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const toast = useToast()

  const { mutate: deleteConversation, isPending: isDeleting } = useDeleteConversationMutation({
    onSuccess: () => {
      setIsDeleteModalOpen(false)
      toast({
        status: "success",
        title: "Chat deleted successfully",
      })
      // Optionally redirect if we're on the deleted chat's page
      if (router.query["conversation-id"] === id) {
        router.push("/ai-agent/chat")
      }
    },
    onError: () => {
      toast({
        status: "error",
        title: "Failed to delete chat",
      })
    },
  })

  const handleClick = () => {
    if (isMobileOnly) {
      onClick?.()
    }
    router.push(`/ai-agent/chat/${id}`)
  }

  const handleDelete = () => {
    deleteConversation(id)
  }

  return (
    <>
      <div
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 cursor-pointer rounded-xl hover:bg-atherGray-700 transition-colors relative group",
          isActive && "bg-atherGray-700",
        )}
        onClick={handleClick}
      >
        <div className="flex-1 min-w-0">
          <div className="flex flex-col">
            <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100 line-clamp-2">{title}</h3>
            <div className="flex items-center justify-between ">
              <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
                {formatTimestamp(timestamp)}
              </span>
              <button
                className="p-1 transition-opacity opacity-0 rounded group-hover:opacity-100 hover:bg-atherGray-600"
                onClick={e => {
                  e.stopPropagation()
                  setIsDeleteModalOpen(true)
                }}
              >
                <DeleteIcon className="text-red-500" />
              </button>
            </div>
          </div>
          {lastMessage && <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{lastMessage}</p>}
        </div>
      </div>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Chat"
        className="max-w-md"
      >
        <p className="mb-2 text-sm text-atherGray-300">Are you sure you want to delete this chat?</p>
        <p className="text-atherGray-300">This action cannot be undone.</p>
        <div className="flex items-center justify-end mt-6 space-x-2">
          <IconButton
            onClick={() => setIsDeleteModalOpen(false)}
            colorScheme="secondary"
            className="px-4 font-semibold"
          >
            Cancel
          </IconButton>
          <IconButton onClick={handleDelete} isLoading={isDeleting} className="px-4 font-semibold">
            Delete
          </IconButton>
        </div>
      </Modal>
    </>
  )
}
